export enum CompanyLegalDocumentType {
  RccmRegistration = "RccmRegistration",
  BusinessOperatingCertificate = "BusinessOperatingCertificate",
  VehicleInsurance = "VehicleInsurance",
  TaxCertificate = "TaxCertificate",
  Other = "Other"
}

export enum CompanySlaStatus {
  Active = "Active",
  Inactive = "Inactive",
  Pending = "Pending"
}

export interface CompanySla {
  id: string;
  deliveryCompanyId: string;
  title: string;
  version: string | null;
  counterparty: string | null;
  contractNumber: string | null;
  status: CompanySlaStatus;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  guaranteedDelivery: string | null;
  successRate: number | null;
  penaltyPerBreach: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertCompanySlaBody {
  title: string;
  version?: string | null;
  counterparty?: string | null;
  contractNumber?: string | null;
  status: CompanySlaStatus;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  guaranteedDelivery?: string | null;
  successRate?: number | null;
  penaltyPerBreach?: number | null;
}

export interface CompanyOperationalContact {
  id: string;
  deliveryCompanyId: string;
  fullName: string;
  role: string | null;
  email: string | null;
  phoneNumber: string | null;
  avatarMediaId: string | null;
  avatar: { id: string; url: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertOperationalContactBody {
  fullName: string;
  role?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  avatarMediaId?: string | null;
}

export interface CompanyCommunicationLog {
  id: string;
  deliveryCompanyId: string;
  title: string;
  date: string;
  createdAt: string;
}

export interface CreateCommunicationLogBody {
  title: string;
  date: string;
}

export interface CompanyLegalDocument {
  id: string;
  deliveryCompanyId: string;
  mediaId: string;
  media: { id: string; url: string } | null;
  type: CompanyLegalDocumentType;
  name: string;
  issuedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface CreateLegalDocumentBody {
  mediaId: string;
  type: CompanyLegalDocumentType;
  name: string;
  issuedAt?: string | null;
  expiresAt?: string | null;
}

export interface UpdateLegalDocumentBody {
  mediaId?: string;
  type: CompanyLegalDocumentType;
  name: string;
  issuedAt?: string | null;
  expiresAt?: string | null;
}

export type DriverPerformanceStatus = "inTransit" | "onShift" | "offShift";

export interface DriverPerformanceRow {
  id: string;
  name: string;
  imageUrl: string | null;
  statusKey: DriverPerformanceStatus;
  /** Orders the driver handled within the selected period. */
  orders: number;
  successRate: number;
  failedRate: number;
}

export interface DriverPerformanceResponse {
  stats: {
    activeDrivers: number;
    totalDrivers: number;
    avgDeliveriesPerDay: number;
    avgSuccessRate: number;
    avgFailureRate: number;
  };
  topPerformers: DriverPerformanceRow[];
  underperforming: DriverPerformanceRow[];
  allDrivers: DriverPerformanceRow[];
}

export interface StatCardData {
  total: number;
  today: number;
  trendPct: number;
}

/** Empty on both ends means all time. */
export interface CompanyPeriodParams {
  from?: string;
  to?: string;
}

export interface RevenueParams {
  from: string;
  to: string;
  breakdownBy: "Day" | "Month";
}

export interface RevenueResponse {
  totalRevenue: number;
  today: { value: number; trendPct: number };
  data: Array<{ date: string; value: number }>;
}

export interface DashboardMetricsResponse {
  statCards: {
    totalOrders: StatCardData;
    totalReturns: StatCardData;
    canceled: StatCardData;
    delivered: StatCardData;
    rescheduled: StatCardData;
  };
  kpis: {
    successRate: { value: number; trendPct: number };
    onTimeDelivery: { value: number; trendPct: number };
    failedRate: {
      value: number;
      trendPct: number;
      failedCount: number;
      totalCount: number;
    };
    avgRating: {
      value: number;
      ratingsCount: number;
      trendPct: number;
    };
  };
  realTime: {
    inTransit: number;
    activeDriversCount: number;
    pendingAssignment: number;
  };
  financial: {
    /** What the drivers took in cash from the customers. */
    moneyCollected: number;
    /** Everything the customers paid for what was delivered. */
    turnover: number;
  };
}
